import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY } from "../../utils/formatters";
import { cargarPersonalEdificio } from "../../utils/personalEdificio";
import {
  FaFileInvoiceDollar,
  FaPlus,
  FaTrash,
  FaUser,
  FaCalendarAlt,
  FaPaperPlane,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, btnGhost, inputBase, pageContainer, pageTitle, modalOverlay } from "../../utils/designTokens";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const RecibosAdmin = () => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const [employees, setEmployees] = useState([]);
  const [employeeBuildings, setEmployeeBuildings] = useState({});
  const [attendance, setAttendance] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [payslipData, setPayslipData] = useState({});
  const [existingPayslips, setExistingPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [showExisting, setShowExisting] = useState(false);

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (!buildingId) return;
    fetchData();
    fetchExistingPayslips();
  }, [buildingId, selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Personal del edificio: employeebuilding por `building` (con el sueldo)
      // + nombre de la ficha users (utils/personalEdificio). Sin escanear la
      // colección employee.
      const emps = await cargarPersonalEdificio(buildingId);
      const ebMap = {};
      emps.forEach((emp) => {
        ebMap[String(emp.employeeid)] = emp.vinculo;
      });
      setEmployeeBuildings(ebMap);
      setEmployees(emps);

      if (emps.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch attendance for the selected month
      const startDate = new Date(selectedYear, selectedMonth, 1);
      const endDate = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);

      const attSnap = await getDocs(
        query(
          collection(db, "attendance"),
          where("buildingid", "==", buildingId)
        )
      );
      const attMap = {};
      attSnap.forEach((d) => {
        const data = d.data();
        const date = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        if (date >= startDate && date <= endDate) {
          const eid = String(data.employeeid);
          if (!attMap[eid]) attMap[eid] = { days: 0, hours: 0 };
          attMap[eid].days += 1;
          attMap[eid].hours += data.hoursWorked || 0;
        }
      });
      setAttendance(attMap);

      // Initialize payslip data for each employee
      const psData = {};
      emps.forEach((emp) => {
        const eid = String(emp.employeeid);
        const eb = ebMap[eid];
        const att = attMap[eid] || { days: 0, hours: 0 };
        psData[eid] = {
          baseSalary: eb?.sueldo || 0,
          daysWorked: att.days,
          totalHours: Number(att.hours.toFixed(1)),
          deductions: [],
          additions: [],
        };
      });
      setPayslipData(psData);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    setLoading(false);
  };

  const fetchExistingPayslips = async () => {
    try {
      const q = query(
        collection(db, "payslips"),
        where("buildingid", "==", buildingId),
        orderBy("generatedAt", "desc")
      );
      const snap = await getDocs(q);
      setExistingPayslips(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching payslips:", err);
    }
  };

  const addDeduction = (empId) => {
    setPayslipData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        deductions: [
          ...(prev[empId]?.deductions || []),
          { concept: "", amount: 0 },
        ],
      },
    }));
  };

  const addAddition = (empId) => {
    setPayslipData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        additions: [
          ...(prev[empId]?.additions || []),
          { concept: "", amount: 0 },
        ],
      },
    }));
  };

  const removeDeduction = (empId, idx) => {
    setPayslipData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        deductions: prev[empId].deductions.filter((_, i) => i !== idx),
      },
    }));
  };

  const removeAddition = (empId, idx) => {
    setPayslipData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        additions: prev[empId].additions.filter((_, i) => i !== idx),
      },
    }));
  };

  const updateDeduction = (empId, idx, field, value) => {
    setPayslipData((prev) => {
      const newDed = [...prev[empId].deductions];
      newDed[idx] = { ...newDed[idx], [field]: field === "amount" ? Number(value) : value };
      return { ...prev, [empId]: { ...prev[empId], deductions: newDed } };
    });
  };

  const updateAddition = (empId, idx, field, value) => {
    setPayslipData((prev) => {
      const newAdd = [...prev[empId].additions];
      newAdd[idx] = { ...newAdd[idx], [field]: field === "amount" ? Number(value) : value };
      return { ...prev, [empId]: { ...prev[empId], additions: newAdd } };
    });
  };

  const calcGross = (empId) => {
    const data = payslipData[empId];
    if (!data) return 0;
    return data.baseSalary + data.additions.reduce((s, a) => s + (a.amount || 0), 0);
  };

  const calcDeductions = (empId) => {
    const data = payslipData[empId];
    if (!data) return 0;
    return data.deductions.reduce((s, d) => s + (d.amount || 0), 0);
  };

  const calcNet = (empId) => {
    return calcGross(empId) - calcDeductions(empId);
  };

  const generatePayslips = async (status = "borrador") => {
    setGenerating(true);
    try {
      const period = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
      for (const emp of employees) {
        const eid = String(emp.employeeid);
        const data = payslipData[eid];
        if (!data) continue;

        await addDoc(collection(db, "payslips"), {
          employeeid: emp.employeeid,
          employeeName: `${emp.nombre || ""} ${emp.apellido || ""}`.trim(),
          buildingid: buildingId,
          period,
          baseSalary: data.baseSalary,
          daysWorked: data.daysWorked,
          totalHours: data.totalHours,
          deductions: data.deductions,
          additions: data.additions,
          grossPay: calcGross(eid),
          netPay: calcNet(eid),
          generatedAt: Timestamp.now(),
          generatedBy: auth.currentUser?.email || "admin",
          status,
        });
      }
      await fetchExistingPayslips();
      showToast(
        status === "borrador"
          ? "Liquidaciones generadas como borrador"
          : "Liquidaciones emitidas correctamente",
        "success"
      );
    } catch (err) {
      console.error("Error generating payslips:", err);
      showToast("Error al generar liquidaciones", "error");
    }
    setGenerating(false);
  };

  const emitPayslip = async (payslipId) => {
    try {
      await updateDoc(doc(db, "payslips", payslipId), {
        status: "emitido",
        emittedAt: Timestamp.now(),
        emittedBy: auth.currentUser?.email || "admin",
      });
      setExistingPayslips((prev) =>
        prev.map((p) => (p.id === payslipId ? { ...p, status: "emitido" } : p))
      );
    } catch (err) {
      console.error("Error emitting payslip:", err);
    }
  };

  const renderStatusBadge = (status) => {
    return <StatusBadge status={status || "borrador"} />;
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaFileInvoiceDollar}
          title="Seleccioná un edificio para gestionar liquidaciones"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaFileInvoiceDollar style={{ marginRight: 10, color: colors.primary }} />
          Liquidaciones
        </h1>
      </div>

      {/* Month/Year Selector */}
      <div style={styles.periodCard}>
        <h3 style={styles.periodTitle}>
          <FaCalendarAlt style={{ marginRight: 8, color: colors.primary }} />
          Generar Liquidaciones del Mes
        </h3>
        <div style={styles.periodRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Mes</label>
            <select
              style={styles.select}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Año</label>
            <select
              style={styles.select}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Employee Payslip Cards */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: colors.gray600, marginTop: 12 }}>Cargando datos...</p>
        </div>
      ) : employees.length === 0 ? (
        <EmptyState
          icon={FaSearch}
          title="No hay empleados en este edificio"
        />
      ) : (
        <>
          <div style={styles.cardList}>
            {employees.map((emp) => {
              const eid = String(emp.employeeid);
              const data = payslipData[eid];
              const isExpanded = expandedEmployee === eid;
              if (!data) return null;

              return (
                <div key={eid} style={styles.payslipCard}>
                  <div
                    style={styles.payslipHeader}
                    onClick={() =>
                      setExpandedEmployee(isExpanded ? null : eid)
                    }
                  >
                    <div style={styles.payslipLeft}>
                      <FaUser style={{ color: colors.primary, marginRight: 8 }} />
                      <span style={{ fontWeight: 400, fontSize: 15 }}>
                        {emp.nombre} {emp.apellido}
                      </span>
                    </div>
                    <div style={styles.payslipRight}>
                      <span style={styles.netPayBadge}>
                        Neto: {formatCurrencyUY(calcNet(eid))}
                      </span>
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={styles.payslipBody}>
                      <div style={styles.payslipGrid}>
                        <div style={styles.payslipStat}>
                          <span style={styles.payslipStatLabel}>Sueldo base</span>
                          <span style={styles.payslipStatValue}>
                            {formatCurrencyUY(data.baseSalary)}
                          </span>
                        </div>
                        <div style={styles.payslipStat}>
                          <span style={styles.payslipStatLabel}>Días trabajados</span>
                          <span style={styles.payslipStatValue}>{data.daysWorked}</span>
                        </div>
                        <div style={styles.payslipStat}>
                          <span style={styles.payslipStatLabel}>Horas totales</span>
                          <span style={styles.payslipStatValue}>{data.totalHours}h</span>
                        </div>
                      </div>

                      {/* Additions */}
                      <div style={styles.sectionHeader}>
                        <span style={{ fontWeight: 400, color: colors.success }}>
                          Adiciones
                        </span>
                        <button
                          style={styles.addRowBtn}
                          onClick={() => addAddition(eid)}
                        >
                          <FaPlus style={{ marginRight: 4 }} /> Agregar
                        </button>
                      </div>
                      {data.additions.map((a, idx) => (
                        <div key={idx} style={styles.lineItemRow}>
                          <input
                            style={styles.lineInput}
                            placeholder="Concepto"
                            value={a.concept}
                            onChange={(e) =>
                              updateAddition(eid, idx, "concept", e.target.value)
                            }
                          />
                          <input
                            style={{ ...styles.lineInput, width: 120 }}
                            type="number"
                            placeholder="Monto"
                            value={a.amount || ""}
                            onChange={(e) =>
                              updateAddition(eid, idx, "amount", e.target.value)
                            }
                          />
                          <button
                            style={styles.removeRowBtn}
                            onClick={() => removeAddition(eid, idx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}

                      {/* Deductions */}
                      <div style={{ ...styles.sectionHeader, marginTop: 16 }}>
                        <span style={{ fontWeight: 400, color: colors.error }}>
                          Deducciones
                        </span>
                        <button
                          style={styles.addRowBtn}
                          onClick={() => addDeduction(eid)}
                        >
                          <FaPlus style={{ marginRight: 4 }} /> Agregar
                        </button>
                      </div>
                      {data.deductions.map((d, idx) => (
                        <div key={idx} style={styles.lineItemRow}>
                          <input
                            style={styles.lineInput}
                            placeholder="Concepto"
                            value={d.concept}
                            onChange={(e) =>
                              updateDeduction(eid, idx, "concept", e.target.value)
                            }
                          />
                          <input
                            style={{ ...styles.lineInput, width: 120 }}
                            type="number"
                            placeholder="Monto"
                            value={d.amount || ""}
                            onChange={(e) =>
                              updateDeduction(eid, idx, "amount", e.target.value)
                            }
                          />
                          <button
                            style={styles.removeRowBtn}
                            onClick={() => removeDeduction(eid, idx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}

                      {/* Summary */}
                      <div style={styles.summaryBox}>
                        <div style={styles.summaryRow}>
                          <span>Bruto</span>
                          <span style={{ fontWeight: 400 }}>
                            {formatCurrencyUY(calcGross(eid))}
                          </span>
                        </div>
                        <div style={styles.summaryRow}>
                          <span>Deducciones</span>
                          <span style={{ fontWeight: 400, color: colors.error }}>
                            -{formatCurrencyUY(calcDeductions(eid))}
                          </span>
                        </div>
                        <div style={styles.summaryDivider} />
                        <div style={styles.summaryRow}>
                          <span style={{ fontWeight: 400, fontSize: 16 }}>
                            Neto
                          </span>
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: typography.sizes.xl,
                              color: colors.primary,
                            }}
                          >
                            {formatCurrencyUY(calcNet(eid))}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Generate Buttons */}
          <div style={styles.generateRow}>
            <button
              style={styles.generateBtn}
              onClick={() => generatePayslips("borrador")}
              disabled={generating}
            >
              <FaFileInvoiceDollar style={{ marginRight: 8 }} />
              {generating ? "Generando..." : "Generar como Borrador"}
            </button>
            <button
              style={styles.emitBtn}
              onClick={() => generatePayslips("emitido")}
              disabled={generating}
            >
              <FaPaperPlane style={{ marginRight: 8 }} />
              {generating ? "Generando..." : "Generar y Emitir"}
            </button>
          </div>
        </>
      )}

      {/* Existing Payslips */}
      <div style={styles.existingSection}>
        <div
          style={styles.existingHeader}
          onClick={() => setShowExisting(!showExisting)}
        >
          <h3 style={{ margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black }}>
            Liquidaciones Anteriores ({existingPayslips.length})
          </h3>
          {showExisting ? <FaChevronUp /> : <FaChevronDown />}
        </div>

        {showExisting && (
          <div style={styles.existingList}>
            {existingPayslips.length === 0 ? (
              <p style={{ textAlign: "center", color: colors.gray400, padding: spacing[5] }}>
                No hay liquidaciones anteriores
              </p>
            ) : (
              existingPayslips.map((ps) => (
                <div key={ps.id} style={styles.existingCard}>
                  <div style={styles.existingCardLeft}>
                    <span style={{ fontWeight: 400 }}>
                      {ps.employeeName}
                    </span>
                    <span style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>
                      Período: {ps.period}
                    </span>
                  </div>
                  <div style={styles.existingCardRight}>
                    <span style={{ fontWeight: 400 }}>
                      {formatCurrencyUY(ps.netPay)}
                    </span>
                    {renderStatusBadge(ps.status)}
                    {ps.status === "borrador" && (
                      <button
                        style={styles.smallEmitBtn}
                        onClick={() => emitPayslip(ps.id)}
                      >
                        Emitir
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Estilos con design tokens - sin valores hardcodeados
const styles = {
  container: {
    ...pageContainer,
  },
  header: {
    marginBottom: spacing[5],
  },
  title: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  periodCard: {
    ...cardBase,
    padding: spacing[5],
    marginBottom: spacing[6],
    overflow: "hidden",
  },
  periodTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    display: "flex",
    alignItems: "center",
    margin: `0 0 ${spacing[4]}px`,
  },
  periodRow: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 160,
  },
  filterLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  select: {
    ...inputBase,
    width: "auto",
    padding: "8px 12px",
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  payslipCard: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  payslipHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    cursor: "pointer",
  },
  payslipLeft: {
    display: "flex",
    alignItems: "center",
  },
  payslipRight: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  netPayBadge: {
    background: colors.infoLight,
    color: colors.primary,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    padding: `${spacing[1]}px ${spacing[3]}px`,
    borderRadius: radius.sm,
  },
  payslipBody: {
    padding: `0 ${spacing[5]}px ${spacing[5]}px`,
    borderTop: `1px solid ${colors.gray100}`,
  },
  payslipGrid: {
    display: "flex",
    gap: spacing[4],
    marginTop: spacing[4],
    marginBottom: spacing[4],
    flexWrap: "wrap",
  },
  payslipStat: {
    flex: 1,
    minWidth: 120,
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  payslipStatLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  payslipStatValue: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[2],
    fontSize: typography.sizes.md,
  },
  addRowBtn: {
    display: "flex",
    alignItems: "center",
    background: "transparent",
    border: `1px dashed ${colors.gray300}`,
    borderRadius: 6,
    padding: `${spacing[1]}px ${spacing[3]}px`,
    fontSize: typography.sizes.sm,
    color: colors.primary,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    transition: `all ${transitions.fast}`,
  },
  lineItemRow: {
    display: "flex",
    gap: spacing[2],
    alignItems: "center",
    marginBottom: 6,
  },
  lineInput: {
    flex: 1,
    padding: "6px 10px",
    borderRadius: 6,
    border: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.base,
    fontFamily: typography.fontFamily,
    outline: "none",
    transition: `border-color ${transitions.fast}`,
  },
  removeRowBtn: {
    ...btnGhost,
    color: colors.error,
    fontSize: typography.sizes.base,
    padding: spacing[1],
  },
  summaryBox: {
    background: colors.gray50,
    borderRadius: radius.md,
    padding: spacing[4],
    marginTop: spacing[4],
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[1]}px 0`,
    fontSize: typography.sizes.md,
    color: colors.gray900,
  },
  summaryDivider: {
    height: 1,
    background: colors.gray200,
    margin: `${spacing[2]}px 0`,
  },
  generateRow: {
    display: "flex",
    gap: spacing[3],
    marginBottom: spacing[8],
    flexWrap: "wrap",
  },
  generateBtn: {
    ...btnSecondary,
    padding: `${spacing[3]}px ${spacing[6]}px`,
    borderRadius: radius.md,
    border: `1px solid ${colors.primary}`,
    color: colors.primary,
    fontSize: 15,
  },
  emitBtn: {
    ...btnPrimary,
    padding: `${spacing[3]}px ${spacing[6]}px`,
    borderRadius: radius.md,
    fontSize: 15,
  },
  existingSection: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  existingHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    cursor: "pointer",
  },
  existingList: {
    borderTop: `1px solid ${colors.gray200}`,
  },
  existingCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[3]}px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray100}`,
    flexWrap: "wrap",
    gap: spacing[2],
  },
  existingCardLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  existingCardRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  smallEmitBtn: {
    ...btnPrimary,
    padding: `${spacing[1]}px ${spacing[3]}px`,
    borderRadius: 6,
    fontSize: typography.sizes.sm,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  spinner: {
    width: 36,
    height: 36,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default RecibosAdmin;
